import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Loader2,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Users,
  Building2,
  Phone,
  Mail,
  DollarSign,
  FileText,
  Calendar,
  UserPlus,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Unions = () => {
  const [loading, setLoading] = useState(true);
  const [unions, setUnions] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("unions");
  
  // Dialogs
  const [createUnionOpen, setCreateUnionOpen] = useState(false);
  const [editUnionOpen, setEditUnionOpen] = useState(false);
  const [viewUnionOpen, setViewUnionOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [createAgreementOpen, setCreateAgreementOpen] = useState(false);
  
  const [selectedUnion, setSelectedUnion] = useState(null);
  const [saving, setSaving] = useState(false);

  // Union form
  const [unionForm, setUnionForm] = useState({
    name: "",
    local_number: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    dues_type: "percentage",
    dues_amount: 0,
    notes: "",
  });

  // Membership form
  const [membershipForm, setMembershipForm] = useState({
    employee_id: "",
    union_id: "",
    membership_number: "",
    join_date: "",
    seniority_date: "",
    classification: "",
    notes: "",
  });

  // Agreement form
  const [agreementForm, setAgreementForm] = useState({
    union_id: "",
    title: "",
    effective_date: "",
    expiry_date: "",
    document_url: "",
    key_terms: "",
    notes: "",
  });

  const fetchUnions = useCallback(async () => {
    try {
      const [unionsRes, agreementsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/unions`),
        axios.get(`${API}/unions/agreements`),
        axios.get(`${API}/employees`),
      ]);
      setUnions(unionsRes.data.unions || []);
      setAgreements(agreementsRes.data.agreements || []);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load unions data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnions();
  }, [fetchUnions]);

  const handleCreateUnion = async (e) => {
    e.preventDefault();
    if (!unionForm.name) {
      toast.error("Union name is required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/unions`, unionForm);
      toast.success("Union created successfully");
      setCreateUnionOpen(false);
      resetUnionForm();
      fetchUnions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create union");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUnion = async (e) => {
    e.preventDefault();
    if (!selectedUnion) return;
    setSaving(true);
    try {
      await axios.put(`${API}/unions/${selectedUnion.id}`, unionForm);
      toast.success("Union updated successfully");
      setEditUnionOpen(false);
      fetchUnions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update union");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUnion = async (union) => {
    if (!window.confirm(`Are you sure you want to archive "${union.name}"?`)) return;
    try {
      await axios.delete(`${API}/unions/${union.id}`);
      toast.success("Union archived successfully");
      fetchUnions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive union");
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!membershipForm.employee_id || !membershipForm.union_id) {
      toast.error("Please select an employee and union");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/unions/memberships`, membershipForm);
      toast.success("Member added successfully");
      setAddMemberOpen(false);
      resetMembershipForm();
      fetchUnions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add member");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAgreement = async (e) => {
    e.preventDefault();
    if (!agreementForm.union_id || !agreementForm.title) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/unions/agreements`, agreementForm);
      toast.success("Agreement created successfully");
      setCreateAgreementOpen(false);
      resetAgreementForm();
      fetchUnions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create agreement");
    } finally {
      setSaving(false);
    }
  };

  const resetUnionForm = () => {
    setUnionForm({
      name: "",
      local_number: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      address: "",
      dues_type: "percentage",
      dues_amount: 0,
      notes: "",
    });
  };

  const resetMembershipForm = () => {
    setMembershipForm({
      employee_id: "",
      union_id: "",
      membership_number: "",
      join_date: "",
      seniority_date: "",
      classification: "",
      notes: "",
    });
  };

  const resetAgreementForm = () => {
    setAgreementForm({
      union_id: "",
      title: "",
      effective_date: "",
      expiry_date: "",
      document_url: "",
      key_terms: "",
      notes: "",
    });
  };

  const openEditUnion = (union) => {
    setSelectedUnion(union);
    setUnionForm({
      name: union.name,
      local_number: union.local_number || "",
      contact_name: union.contact_name || "",
      contact_email: union.contact_email || "",
      contact_phone: union.contact_phone || "",
      address: union.address || "",
      dues_type: union.dues_type || "percentage",
      dues_amount: union.dues_amount || 0,
      notes: union.notes || "",
    });
    setEditUnionOpen(true);
  };

  const openViewUnion = async (union) => {
    try {
      const response = await axios.get(`${API}/unions/${union.id}`);
      setSelectedUnion(response.data);
      setViewUnionOpen(true);
    } catch (error) {
      toast.error("Failed to load union details");
    }
  };

  const filteredUnions = unions.filter(
    (u) =>
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.local_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-8 w-8 text-primary" />
            Union Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage unions, memberships, and collective agreements
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAddMemberOpen(true)} data-testid="add-member-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Add Member
          </Button>
          <Button onClick={() => setCreateUnionOpen(true)} data-testid="create-union-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Union
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Unions</p>
                <p className="text-2xl font-bold">{unions.length}</p>
              </div>
              <Building2 className="h-8 w-8 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Members</p>
                <p className="text-2xl font-bold">
                  {unions.reduce((acc, u) => acc + (u.member_count || 0), 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Agreements</p>
                <p className="text-2xl font-bold">{agreements.length}</p>
              </div>
              <FileText className="h-8 w-8 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Est. Monthly Dues</p>
                <p className="text-2xl font-bold">
                  ${unions.reduce((acc, u) => {
                    if (u.dues_type === "flat") return acc + (u.dues_amount || 0) * (u.member_count || 0);
                    return acc + ((u.dues_amount || 0) / 100) * 5000 * (u.member_count || 0); // Rough estimate
                  }, 0).toLocaleString()}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-yellow-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="unions" data-testid="tab-unions">Unions</TabsTrigger>
          <TabsTrigger value="agreements" data-testid="tab-agreements">Collective Agreements</TabsTrigger>
        </TabsList>

        {/* Unions Tab */}
        <TabsContent value="unions" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search unions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="union-search"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Unions</CardTitle>
              <CardDescription>
                {filteredUnions.length} {filteredUnions.length === 1 ? "union" : "unions"} registered
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredUnions.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No unions found</h3>
                  <p className="text-muted-foreground mb-4">
                    Add your first union to manage memberships
                  </p>
                  <Button onClick={() => setCreateUnionOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Union
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Local #</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Dues</TableHead>
                      <TableHead>Members</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUnions.map((union) => (
                      <TableRow key={union.id} data-testid={`union-row-${union.id}`}>
                        <TableCell className="font-medium">{union.name}</TableCell>
                        <TableCell>{union.local_number || "-"}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm">{union.contact_name || "-"}</span>
                            {union.contact_email && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {union.contact_email}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {union.dues_type === "percentage"
                            ? `${union.dues_amount}%`
                            : `$${union.dues_amount?.toFixed(2)}`}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{union.member_count || 0}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openViewUnion(union)}
                              data-testid={`view-union-${union.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditUnion(union)}
                              data-testid={`edit-union-${union.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUnion(union)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agreements Tab */}
        <TabsContent value="agreements" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Collective Agreements</CardTitle>
                <CardDescription>Manage collective bargaining agreements</CardDescription>
              </div>
              <Button onClick={() => setCreateAgreementOpen(true)} size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add Agreement
              </Button>
            </CardHeader>
            <CardContent>
              {agreements.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium">No agreements found</h3>
                  <p className="text-muted-foreground">
                    Add collective agreements to track contract details
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Title</TableHead>
                      <TableHead>Union</TableHead>
                      <TableHead>Effective Date</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agreements.map((agreement) => {
                      const isExpired = agreement.expiry_date && new Date(agreement.expiry_date) < new Date();
                      const isExpiringSoon = agreement.expiry_date && 
                        new Date(agreement.expiry_date) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) &&
                        !isExpired;
                      
                      return (
                        <TableRow key={agreement.id}>
                          <TableCell className="font-medium">{agreement.title}</TableCell>
                          <TableCell>{agreement.union_name}</TableCell>
                          <TableCell>
                            {agreement.effective_date
                              ? new Date(agreement.effective_date).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {agreement.expiry_date
                              ? new Date(agreement.expiry_date).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {isExpired ? (
                              <Badge variant="destructive">Expired</Badge>
                            ) : isExpiringSoon ? (
                              <Badge variant="outline" className="text-orange-500 border-orange-500">
                                Expiring Soon
                              </Badge>
                            ) : (
                              <Badge className="bg-green-500">Active</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Union Dialog */}
      <Dialog open={createUnionOpen} onOpenChange={setCreateUnionOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Union</DialogTitle>
            <DialogDescription>Register a new union for your organization</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUnion} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Union Name *</Label>
                <Input
                  value={unionForm.name}
                  onChange={(e) => setUnionForm({ ...unionForm, name: e.target.value })}
                  placeholder="e.g., IBEW Local 213"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Local Number</Label>
                <Input
                  value={unionForm.local_number}
                  onChange={(e) => setUnionForm({ ...unionForm, local_number: e.target.value })}
                  placeholder="e.g., 213"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={unionForm.contact_name}
                  onChange={(e) => setUnionForm({ ...unionForm, contact_name: e.target.value })}
                  placeholder="Primary contact person"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Phone</Label>
                <Input
                  value={unionForm.contact_phone}
                  onChange={(e) => setUnionForm({ ...unionForm, contact_phone: e.target.value })}
                  placeholder="(604) 555-0100"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={unionForm.contact_email}
                onChange={(e) => setUnionForm({ ...unionForm, contact_email: e.target.value })}
                placeholder="contact@union.org"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={unionForm.address}
                onChange={(e) => setUnionForm({ ...unionForm, address: e.target.value })}
                placeholder="Union office address"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dues Type</Label>
                <Select
                  value={unionForm.dues_type}
                  onValueChange={(v) => setUnionForm({ ...unionForm, dues_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage of Gross</SelectItem>
                    <SelectItem value="flat">Flat Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Dues Amount {unionForm.dues_type === "percentage" ? "(%)" : "($)"}</Label>
                <Input
                  type="number"
                  step={unionForm.dues_type === "percentage" ? "0.1" : "0.01"}
                  value={unionForm.dues_amount}
                  onChange={(e) => setUnionForm({ ...unionForm, dues_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={unionForm.notes}
                onChange={(e) => setUnionForm({ ...unionForm, notes: e.target.value })}
                placeholder="Additional information about this union"
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setCreateUnionOpen(false);
                resetUnionForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Union
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Union Dialog */}
      <Dialog open={editUnionOpen} onOpenChange={setEditUnionOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Union</DialogTitle>
            <DialogDescription>Update union information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUnion} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Union Name *</Label>
                <Input
                  value={unionForm.name}
                  onChange={(e) => setUnionForm({ ...unionForm, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Local Number</Label>
                <Input
                  value={unionForm.local_number}
                  onChange={(e) => setUnionForm({ ...unionForm, local_number: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dues Type</Label>
                <Select
                  value={unionForm.dues_type}
                  onValueChange={(v) => setUnionForm({ ...unionForm, dues_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage of Gross</SelectItem>
                    <SelectItem value="flat">Flat Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Dues Amount</Label>
                <Input
                  type="number"
                  step={unionForm.dues_type === "percentage" ? "0.1" : "0.01"}
                  value={unionForm.dues_amount}
                  onChange={(e) => setUnionForm({ ...unionForm, dues_amount: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditUnionOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Union Dialog */}
      <Dialog open={viewUnionOpen} onOpenChange={setViewUnionOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedUnion?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedUnion?.local_number && `Local ${selectedUnion.local_number}`}
            </DialogDescription>
          </DialogHeader>
          {selectedUnion && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Contact</p>
                  <p className="font-medium">{selectedUnion.contact_name || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone</p>
                  <p className="font-medium">{selectedUnion.contact_phone || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedUnion.contact_email || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Dues</p>
                  <p className="font-medium">
                    {selectedUnion.dues_type === "percentage"
                      ? `${selectedUnion.dues_amount}% of gross`
                      : `$${selectedUnion.dues_amount?.toFixed(2)} flat`}
                  </p>
                </div>
              </div>
              
              {selectedUnion.address && (
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{selectedUnion.address}</p>
                </div>
              )}

              <div>
                <h4 className="font-medium mb-2">Members ({selectedUnion.members?.length || 0})</h4>
                {selectedUnion.members?.length > 0 ? (
                  <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                    {selectedUnion.members.map((member, idx) => (
                      <div key={idx} className="p-3 flex justify-between items-center">
                        <span>{member.employee_name || member.employee_id}</span>
                        <Badge variant="outline">{member.classification || "Member"}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No members yet</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewUnionOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Union Member</DialogTitle>
            <DialogDescription>Add an employee to a union</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddMember} className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select
                value={membershipForm.employee_id}
                onValueChange={(v) => setMembershipForm({ ...membershipForm, employee_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Union *</Label>
              <Select
                value={membershipForm.union_id}
                onValueChange={(v) => setMembershipForm({ ...membershipForm, union_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select union" />
                </SelectTrigger>
                <SelectContent>
                  {unions.map((union) => (
                    <SelectItem key={union.id} value={union.id}>
                      {union.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Membership Number</Label>
                <Input
                  value={membershipForm.membership_number}
                  onChange={(e) => setMembershipForm({ ...membershipForm, membership_number: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Classification</Label>
                <Input
                  value={membershipForm.classification}
                  onChange={(e) => setMembershipForm({ ...membershipForm, classification: e.target.value })}
                  placeholder="e.g., Journeyman"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Join Date</Label>
                <Input
                  type="date"
                  value={membershipForm.join_date}
                  onChange={(e) => setMembershipForm({ ...membershipForm, join_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Seniority Date</Label>
                <Input
                  type="date"
                  value={membershipForm.seniority_date}
                  onChange={(e) => setMembershipForm({ ...membershipForm, seniority_date: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setAddMemberOpen(false);
                resetMembershipForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Member
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Agreement Dialog */}
      <Dialog open={createAgreementOpen} onOpenChange={setCreateAgreementOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Collective Agreement</DialogTitle>
            <DialogDescription>Register a collective bargaining agreement</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAgreement} className="space-y-4">
            <div className="space-y-2">
              <Label>Union *</Label>
              <Select
                value={agreementForm.union_id}
                onValueChange={(v) => setAgreementForm({ ...agreementForm, union_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select union" />
                </SelectTrigger>
                <SelectContent>
                  {unions.map((union) => (
                    <SelectItem key={union.id} value={union.id}>
                      {union.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Agreement Title *</Label>
              <Input
                value={agreementForm.title}
                onChange={(e) => setAgreementForm({ ...agreementForm, title: e.target.value })}
                placeholder="e.g., 2024-2027 Collective Agreement"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Effective Date *</Label>
                <Input
                  type="date"
                  value={agreementForm.effective_date}
                  onChange={(e) => setAgreementForm({ ...agreementForm, effective_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date *</Label>
                <Input
                  type="date"
                  value={agreementForm.expiry_date}
                  onChange={(e) => setAgreementForm({ ...agreementForm, expiry_date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Key Terms</Label>
              <Textarea
                value={agreementForm.key_terms}
                onChange={(e) => setAgreementForm({ ...agreementForm, key_terms: e.target.value })}
                placeholder="Summary of key contract terms..."
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setCreateAgreementOpen(false);
                resetAgreementForm();
              }}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Agreement
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Unions;
